/**
 * Every fan-facing string the Pick'em surfaces render (ISSUE-1303 to
 * ISSUE-1306, ISSUE-1309, ISSUE-1310), in one module.
 *
 * Two word rules hold here. A spread is pick context only, so no wagering
 * word ever appears. And "titles" is banned from copy (GLOSSARY-5): a round
 * win is a "round won", never a title, even though the API field is `titles`.
 */

// The create flow (ISSUE-1303).
export const PICKEM_SETUP_STEP_TITLE = "Pick'em setup";
export const PICKEM_SETUP_NO_LEAGUE = "Choose a league first.";
export const PICKEM_SETUP_LOADING = "Loading Pick'em options";
export const PICKEM_SETUP_FAILED =
  "We could not load the Pick'em options for this league.";
export const PICKEM_SETUP_RETRY = "Try again";
export const PICKEM_RULES_PREVIEW_HEADING = "What your players will read";
export const PICKEM_RULES_PREVIEW_PENDING =
  "Finish the choices above to see the rules your players will read.";

// The settings fields, shared by the create step and the Settings dialog.
export const PICKEM_SCORING_LABEL = "How picks score";
export const PICKEM_SCORING_STANDARD_LABEL = "Standard";
export const PICKEM_SCORING_STANDARD_DESCRIPTION =
  "Every correct pick earns the same points.";
export const PICKEM_SCORING_CONFIDENCE_LABEL = "Confidence points";
export const PICKEM_SCORING_CONFIDENCE_DESCRIPTION =
  "Players rank their picks from most to least sure. A correct pick earns its rank.";
export const PICKEM_WIN_POINTS_LABEL = "Points for a correct pick";
export const PICKEM_LOSS_POINTS_LABEL = "Points for a wrong pick";
export const PICKEM_LOSS_POINTS_HINT =
  "Most pools leave this at 0. A missed pick counts as a wrong one.";
export const PICKEM_PICKS_MODE_LABEL = "Games to pick each round";
export const PICKEM_PICKS_MODE_ALL_LABEL = "Every game";
export const PICKEM_PICKS_MODE_COUNT_LABEL = "A set number";
export const PICKEM_PICKS_COUNT_LABEL = "How many games";
export const PICKEM_BASIS_LABEL = "How picks are made";
export const PICKEM_BASIS_MONEYLINE_LABEL = "Pick the winner (moneyline)";
export const PICKEM_BASIS_SPREAD_LABEL = "Beat the spread";
export const PICKEM_BASIS_SPREAD_DESCRIPTION =
  "Each pick keeps the line it had when it was saved.";
export const PICKEM_BASIS_NO_SPREADS_DESCRIPTION =
  "This league has no spreads, so picks are straight winners.";
export const PICKEM_GROUPING_LABEL = "How games make a round";
export const PICKEM_GROUPING_WEEK_LABEL = "By week";
export const PICKEM_GROUPING_DAY_LABEL = "By game day";
export const PICKEM_WEEK_START_LABEL = "Week starts on";
export const PICKEM_START_DATE_LABEL = "Start date";
export const PICKEM_END_MODE_LABEL = "End date";
export const PICKEM_END_MODE_TBD_LABEL = "TBD";
export const PICKEM_END_MODE_TBD_DESCRIPTION = "Runs until the season ends.";
export const PICKEM_END_MODE_DATE_LABEL = "Pick a date";
export const PICKEM_END_DATE_LABEL = "Last day of the contest";
export const PICKEM_TIE_LABEL =
  "A tie, or a game that lands exactly on the spread";
export const PICKEM_TIE_VOID_LABEL = "Doesn't count";
export const PICKEM_TIE_VOID_DESCRIPTION =
  "No points, and it is not a win or a loss.";
export const PICKEM_TIE_WIN_LABEL = "Counts as a win";
export const PICKEM_TIE_LOSS_LABEL = "Counts as a loss";
export const PICKEM_TIEBREAKER_LABEL = "Round tiebreaker";
export const PICKEM_TIEBREAKER_NONE_LABEL = "None";
export const PICKEM_TIEBREAKER_NONE_DESCRIPTION =
  "Players tied on points share the round.";
export const PICKEM_TIEBREAKER_TOTAL_LABEL = "Guess the total points";
export const PICKEM_TIEBREAKER_TOTAL_DESCRIPTION =
  "Everyone guesses the combined final score of the round's last game. The closest guess wins a tied round.";
export const PICKEM_NUMBER_REQUIRED = "Enter a number.";
export const PICKEM_FROZEN_NOTE =
  "Scoring and how picks are made can't change once the first round opens.";

export const PICKEM_WEEK_DAYS = [
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
  { value: "7", label: "Sunday" },
] as const;

// The Game tab (ISSUE-1304, ISSUE-1309).
export const PICKEM_ROUNDS_LABEL = "Rounds";
export const PICKEM_GAME_LOADING = "Loading this round";
export const PICKEM_GAME_FAILED = "We could not load this round.";
export const PICKEM_RETRY = "Try again";
export const PICKEM_GAMES_HEADING = "Games";
export const PICKEM_NO_GAMES = "There are no games in this round.";
export const PICKEM_SAVE_LABEL = "Save picks";
export const PICKEM_SAVING_LABEL = "Saving picks";
export const PICKEM_SAVED_LABEL = "Picks saved";
export const PICKEM_YOUR_PICK_LABEL = "Your pick";
export const PICKEM_LOCKED_LABEL = "Locked";
export const PICKEM_ALL_PICKS_IN = "All your picks are in.";
export const PICKEM_CONFIDENCE_HEADING = "Your confidence order";
export const PICKEM_CONFIDENCE_HINT =
  "Most sure at the top. A correct pick earns its rank.";
export const PICKEM_CONFIDENCE_EMPTY =
  "Pick a team and it lands here with the next free rank.";
export const PICKEM_TIEBREAKER_HEADING = "Tiebreaker";
export const PICKEM_TIEBREAKER_HINT =
  "Guess the combined final score. It locks when the game starts.";
export const PICKEM_TIEBREAKER_RANGE_ERROR = "Enter a number from 0 to 999.";
export const PICKEM_TIEBREAKER_LOCKED_NONE = "No guess was made.";
export const PICKEM_PICKS_FULL_NOTE =
  "You've made every pick this round allows. Tap one of your picks to clear it, then choose this game.";

export const PICKEM_UNAVAILABLE_LABELS = {
  locked: "Locked",
  postponed: "Postponed",
  canceled: "Called off",
  beforeEntry: "Started before you joined",
} as const;

export const PICKEM_CANNOT_PICK_NOTES = {
  notEntered: "You're not in this contest, so there's nothing to pick.",
  roundNotOpen: "This round isn't open yet. Picks open soon.",
  roundLocked: "This round is locked. Here's what you picked.",
  contestEnded: "This contest is over. Here's what you picked.",
} as const;

export const PICKEM_EMPTY_STATE_NOTES = {
  noContest: "There's no Pick'em contest running right now.",
  noRoundsYet:
    "The first round isn't ready yet. It opens closer to the first game.",
  contestEnded:
    "This contest is over. The Results tab has the final standings.",
} as const;

export const PICKEM_OUTCOME_LABELS = {
  win: "Correct",
  loss: "Wrong",
  push: "Tie",
  pending: "Pending",
} as const;

export function pickemRoundChipLabel(ordinal: number): string {
  return `Round ${ordinal}`;
}

export function pickemProgressLine(input: {
  made: number;
  required: number;
}): string {
  return `${input.made} of ${input.required} picks made`;
}

export function pickemStillNeededLine(count: number): string {
  return count === 1 ? "1 more pick to make" : `${count} more picks to make`;
}

export function pickemLocksAtLine(timeLabel: string): string {
  return `Locks ${timeLabel}`;
}

export function pickemLocksInLine(countdownLabel: string): string {
  return `Locks in ${countdownLabel}`;
}

export function pickemNextLockLine(timeLabel: string): string {
  return `Next game locks ${timeLabel}`;
}

export function pickemRankLabel(rank: number): string {
  return `Rank ${rank}`;
}

export function pickemPointsLabel(points: number): string {
  return points === 1 ? "1 pt" : `${points} pts`;
}

export function pickemTiebreakerGameLine(gameLabel: string): string {
  return `Total points in ${gameLabel}`;
}

export function pickemMoveUpLabel(teamName: string): string {
  return `Move ${teamName} up`;
}

export function pickemMoveDownLabel(teamName: string): string {
  return `Move ${teamName} down`;
}

export function pickemMyScoreLine(input: {
  points: number;
  wins: number;
  losses: number;
}): string {
  return `${pickemPointsLabel(input.points)} this round, ${input.wins}-${input.losses}`;
}

// The Results tab (ISSUE-1305).
export const PICKEM_RESULTS_VIEW_LABEL = "Show";
export const PICKEM_RESULTS_VIEW_ROUND = "Round";
export const PICKEM_RESULTS_VIEW_STANDINGS = "Standings";
export const PICKEM_RESULTS_ROUND_LABEL = "Round";
export const PICKEM_RESULTS_MEMBER_LABEL = "Member";
export const PICKEM_RESULTS_EVERYONE = "Everyone";
export const PICKEM_RESULTS_LOADING = "Loading results";
export const PICKEM_RESULTS_FAILED = "We could not load these results.";
export const PICKEM_RESULTS_EMPTY =
  "No results yet. Picks show here as each game starts.";
export const PICKEM_RESULTS_ROUND_WINNER = "Round winner";
export const PICKEM_RESULTS_HIDDEN = "Hidden until kickoff";
export const PICKEM_RESULTS_NO_PICK = "No pick";
export const PICKEM_RESULTS_MISSED = "Missed";
export const PICKEM_RESULTS_YOU = "You";
export const PICKEM_STANDINGS_SCOPE_LABEL = "Standings for";
export const PICKEM_STANDINGS_SCOPE_CONTEST = "This contest";
export const PICKEM_STANDINGS_SCOPE_SEASON = "This season";
export const PICKEM_STANDINGS_CONTEST_LABEL = "Contest";
export const PICKEM_STANDINGS_SEASON_LABEL = "Season";
export const PICKEM_STANDINGS_LOADING = "Loading standings";
export const PICKEM_STANDINGS_FAILED = "We could not load the standings.";
export const PICKEM_STANDINGS_EMPTY =
  "No standings yet. They fill in once the first round is scored.";
export const PICKEM_STANDINGS_RANK_HEADER = "Rank";
export const PICKEM_STANDINGS_MEMBER_HEADER = "Member";
export const PICKEM_STANDINGS_POINTS_HEADER = "Pts";
export const PICKEM_STANDINGS_RECORD_HEADER = "W-L";
export const PICKEM_STANDINGS_ROUNDS_WON_HEADER = "Rounds won";
export const PICKEM_STANDINGS_CAPTION =
  "Ranked by points, then rounds won, then correct picks.";

export function pickemRecordLabel(input: {
  wins: number;
  losses: number;
  pushes: number;
}): string {
  return input.pushes > 0
    ? `${input.wins}-${input.losses}-${input.pushes}`
    : `${input.wins}-${input.losses}`;
}

export function pickemRoundRankLabel(rank: number): string {
  return `#${rank}`;
}

export function pickemTiebreakerGuessLine(input: {
  guess: number;
  diff: number | null;
}): string {
  return input.diff === null
    ? `Tiebreaker guess: ${input.guess}`
    : `Tiebreaker guess: ${input.guess}, off by ${input.diff}`;
}

export function pickemActualTotalLine(total: number): string {
  return `Tiebreaker game total: ${total}`;
}

export function pickemMissedLine(count: number): string {
  return count === 1 ? "1 missed" : `${count} missed`;
}

// The Settings tab (ISSUE-1306, ISSUE-1310).
export const PICKEM_SETTINGS_HEADING = "Pick'em rules";
export const PICKEM_SETTINGS_LOADING = "Loading the Pick'em rules";
export const PICKEM_SETTINGS_FAILED = "We could not load the Pick'em rules.";
export const PICKEM_SETTINGS_NO_CONTEST =
  "There's no Pick'em contest running, so there are no rules to show yet.";
export const PICKEM_SETTINGS_EDIT_LABEL = "Change Pick'em settings";
export const PICKEM_SETTINGS_DIALOG_NAME = "pickem-settings";
export const PICKEM_SETTINGS_DIALOG_TITLE = "Pick'em settings";
export const PICKEM_SETTINGS_SAVE_LABEL = "Save settings";
export const PICKEM_SETTINGS_SAVING_LABEL = "Saving settings";
export const PICKEM_SETTINGS_CANCEL_LABEL = "Cancel";
export const PICKEM_SETTINGS_DISCARD_TITLE = "Discard your changes?";
export const PICKEM_SETTINGS_DISCARD_BODY =
  "The Pick'em settings you changed won't be saved.";
export const PICKEM_SETTINGS_DISCARD_CONFIRM = "Discard";
export const PICKEM_SETTINGS_DISCARD_CANCEL = "Keep editing";
export const PICKEM_SETTINGS_SAVED_NOW = "Saved. The new settings are in.";

export function pickemPendingChangeLine(roundLabel: string): string {
  return `Your latest changes start with ${roundLabel}.`;
}

export function pickemSettingsSavedLine(roundLabel: string | null): string {
  return roundLabel === null
    ? PICKEM_SETTINGS_SAVED_NOW
    : `Saved. The changes start with ${roundLabel}.`;
}

export const PICKEM_MISSING_HEADING = "Still picking";
export const PICKEM_MISSING_LOADING = "Loading who still needs to pick";
export const PICKEM_MISSING_FAILED =
  "We could not load who still needs to pick.";
export const PICKEM_MISSING_EMPTY = "Everyone has made their picks.";
export const PICKEM_MISSING_NUDGE_LABEL = "Nudge";
export const PICKEM_MISSING_NUDGING_LABEL = "Sending nudge";
export const PICKEM_MISSING_NUDGED_LABEL = "Nudged";
export const PICKEM_MISSING_MORE_LABEL = "Show more";

export function pickemMissingCountLine(input: {
  made: number;
  required: number;
}): string {
  return `${input.made} of ${input.required} picks`;
}

export function pickemNudgeLabel(displayName: string): string {
  return `Nudge ${displayName}`;
}
